import React, { useState } from "react";
import { Building2, Users, FileText, CheckCircle, Phone, Mail, HandHeart, Award, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Partnerships = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    facilityName: "",
    contactPerson: "",
    title: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    facilityType: "",
    licenseNumber: "",
    patientVolume: "",
    currentTransportProvider: "",
    servicesNeeded: [],
    preferredContact: "",
    partnershipGoals: "",
    additionalInfo: "",
    consentToContact: false,
    agreeToBaa: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceChange = (service, checked) => {
    setFormData(prev => ({
      ...prev,
      servicesNeeded: checked 
        ? [...prev.servicesNeeded, service]
        : prev.servicesNeeded.filter(s => s !== service)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/partnership-request`, formData);
      
      if (response.data.success) {
        toast.success("Partnership request submitted! We'll contact you within 24 hours to discuss opportunities.");
        // Reset form
        setFormData({
          facilityName: "",
          contactPerson: "",
          title: "",
          phone: "",
          email: "",
          address: "",
          city: "",
          state: "",
          zipCode: "",
          facilityType: "",
          licenseNumber: "",
          patientVolume: "",
          currentTransportProvider: "",
          servicesNeeded: [],
          preferredContact: "",
          partnershipGoals: "",
          additionalInfo: "",
          consentToContact: false,
          agreeToBaa: false
        });
      }
    } catch (error) {
      console.error("Partnership request error:", error);
      const errorMessage = error.response?.data?.detail || 
                          "There was an error submitting your partnership request. Please call us at (269) 248-3514.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <CheckCircle className="h-8 w-8 text-green-600" />,
      title: "HIPAA Compliant Partner",
      description: "Fully HIPAA-compliant transportation services with signed Business Associate Agreements"
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: "Licensed & Insured",
      description: "State-licensed medical transport with comprehensive commercial insurance coverage"
    },
    {
      icon: <Users className="h-8 w-8 text-purple-600" />,
      title: "Professional Drivers",
      description: "Background-checked, trained drivers experienced in medical transportation"
    },
    {
      icon: <Award className="h-8 w-8 text-amber-600" />,
      title: "Veteran-Owned Excellence",
      description: "Military precision and reliability in every aspect of our service"
    }
  ];

  const services = [
    "Wheelchair Accessible Transportation",
    "Ambulatory Patient Transport", 
    "Dialysis Transportation",
    "Discharge Transportation",
    "Recurring Appointment Transport",
    "Long-Distance Medical Transport",
    "Emergency Backup Services",
    "Insurance Billing Assistance"
  ];

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Healthcare Provider Partnerships</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Partner with Quality Transport Services to provide reliable, HIPAA-compliant 
            medical transportation for your patients. Veteran-owned and family-operated 
            with a commitment to excellence in healthcare support services.
          </p>
        </div>

        {/* Partnership Benefits */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Partner With Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="mb-4 flex justify-center">{benefit.icon}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Services We Provide */}
        <section className="mb-12">
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Transportation Services for Healthcare Facilities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Patient Transportation:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Hospital discharge transportation</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Recurring dialysis appointments</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Specialist consultations</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Rehabilitation and therapy appointments</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Partnership Benefits:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Reduced patient no-shows</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Improved patient satisfaction</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">Streamlined discharge process</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">24/7 on-call availability</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* HIPAA & Compliance */}
        <section className="mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="h-6 w-6 text-green-600" />
                  <span>HIPAA Compliance</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-700">
                  <li>• Signed Business Associate Agreements (BAA)</li>
                  <li>• HIPAA-trained staff and drivers</li>
                  <li>• Secure handling of Protected Health Information</li>
                  <li>• Compliance audits and documentation</li>
                  <li>• Incident reporting and breach protocols</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-6 w-6 text-purple-600" />
                  <span>Insurance & Billing</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-700">
                  <li>• Direct insurance billing capabilities</li>
                  <li>• Medicare and Medicaid certified</li>
                  <li>• Prior authorization assistance</li>
                  <li>• Claims processing and follow-up</li>
                  <li>• Detailed reporting for facilities</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Partnership Request Form */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">Request Partnership Information</CardTitle>
              <p className="text-gray-600">
                Tell us about your facility and transportation needs. We'll contact you to discuss how we can support your patients.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Facility Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Facility Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="facilityName">Facility Name *</Label>
                      <Input
                        id="facilityName"
                        type="text"
                        value={formData.facilityName}
                        onChange={(e) => handleInputChange("facilityName", e.target.value)}
                        placeholder="Healthcare facility or organization name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="facilityType">Facility Type *</Label>
                      <Select value={formData.facilityType} onValueChange={(value) => handleInputChange("facilityType", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select facility type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hospital">Hospital</SelectItem>
                          <SelectItem value="clinic">Medical Clinic</SelectItem>
                          <SelectItem value="dialysis">Dialysis Center</SelectItem>
                          <SelectItem value="rehabilitation">Rehabilitation Center</SelectItem>
                          <SelectItem value="nursing-home">Nursing Home</SelectItem>
                          <SelectItem value="assisted-living">Assisted Living Facility</SelectItem>
                          <SelectItem value="physician-practice">Physician Practice</SelectItem>
                          <SelectItem value="imaging">Imaging Center</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Facility Address *</Label>
                    <Input
                      id="address"
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      placeholder="Street address"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        placeholder="MI"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zipCode">ZIP Code *</Label>
                      <Input
                        id="zipCode"
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange("zipCode", e.target.value)}
                        placeholder="49001"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">State License Number</Label>
                    <Input
                      id="licenseNumber"
                      type="text"
                      value={formData.licenseNumber}
                      onChange={(e) => handleInputChange("licenseNumber", e.target.value)}
                      placeholder="State healthcare facility license number"
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contactPerson">Contact Person *</Label>
                      <Input
                        id="contactPerson"
                        type="text"
                        value={formData.contactPerson}
                        onChange={(e) => handleInputChange("contactPerson", e.target.value)}
                        placeholder="Primary contact name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="title">Title/Position *</Label>
                      <Input
                        id="title"
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange("title", e.target.value)}
                        placeholder="Administrator, Director, Manager, etc."
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder="(269) 123-4567"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="contact@facility.com"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Transportation Needs */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Transportation Needs</h3>
                  
                  <div className="space-y-3">
                    <Label>Services Needed (Select all that apply):</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {services.map((service) => (
                        <div key={service} className="flex items-center space-x-2">
                          <Checkbox
                            id={service}
                            checked={formData.servicesNeeded.includes(service)}
                            onCheckedChange={(checked) => handleServiceChange(service, checked)}
                          />
                          <Label htmlFor={service} className="text-sm">{service}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="patientVolume">Estimated Monthly Patient Volume</Label>
                      <Select value={formData.patientVolume} onValueChange={(value) => handleInputChange("patientVolume", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select volume range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-10">1-10 patients</SelectItem>
                          <SelectItem value="11-25">11-25 patients</SelectItem>
                          <SelectItem value="26-50">26-50 patients</SelectItem>
                          <SelectItem value="51-100">51-100 patients</SelectItem>
                          <SelectItem value="100+">100+ patients</SelectItem>
                          <SelectItem value="varies">Varies significantly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="currentTransportProvider">Current Transportation Provider</Label>
                      <Input
                        id="currentTransportProvider"
                        type="text"
                        value={formData.currentTransportProvider}
                        onChange={(e) => handleInputChange("currentTransportProvider", e.target.value)}
                        placeholder="Current provider (if any) or 'None'"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="partnershipGoals">Partnership Goals & Expectations</Label>
                    <Textarea
                      id="partnershipGoals"
                      value={formData.partnershipGoals}
                      onChange={(e) => handleInputChange("partnershipGoals", e.target.value)}
                      placeholder="What are you hoping to achieve with a transportation partnership? Any specific requirements or challenges?"
                      rows={4}
                    />
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Additional Information</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="preferredContact">Preferred Contact Method</Label>
                    <Select value={formData.preferredContact} onValueChange={(value) => handleInputChange("preferredContact", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="How would you like us to contact you?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="either">Either phone or email</SelectItem>
                        <SelectItem value="in-person">In-person meeting</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="additionalInfo">Additional Comments</Label>
                    <Textarea
                      id="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={(e) => handleInputChange("additionalInfo", e.target.value)}
                      placeholder="Any additional information about your facility or transportation needs"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Consent */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Consent & Agreements</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="consentToContact"
                        checked={formData.consentToContact}
                        onCheckedChange={(checked) => handleInputChange("consentToContact", checked)}
                      />
                      <Label htmlFor="consentToContact" className="text-sm leading-relaxed">
                        I consent to be contacted by Quality Transport Services regarding partnership opportunities and transportation services. *
                      </Label>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="agreeToBaa"
                        checked={formData.agreeToBaa}
                        onCheckedChange={(checked) => handleInputChange("agreeToBaa", checked)}
                      />
                      <Label htmlFor="agreeToBaa" className="text-sm leading-relaxed">
                        I understand that any partnership will include a signed HIPAA Business Associate Agreement (BAA) to ensure proper handling of Protected Health Information. *
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-blue-900 hover:bg-blue-800"
                    disabled={isSubmitting || !formData.consentToContact || !formData.agreeToBaa}
                  >
                    {isSubmitting ? "Submitting Request..." : "Request Partnership Information"}
                  </Button>
                  <p className="text-sm text-gray-500 mt-3 text-center">
                    We'll review your request and contact you within 24 hours to discuss partnership opportunities.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </section>

        {/* Contact CTA */}
        <div className="mt-12 bg-blue-900 text-white rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Ready to Partner With Us?</h3>
          <p className="text-blue-100 mb-6">
            Contact us directly to discuss how Quality Transport Services can support your facility 
            and improve patient care through reliable medical transportation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:(269) 248-3514" className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
              Call (269) 248-3514
            </a>
            <a href="mailto:qualitytransportservices42@gmail.com" className="border border-white text-white hover:bg-white hover:text-blue-900 px-6 py-3 rounded-lg font-semibold transition-colors">
              Email Partnership Team
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Partnerships;